import { useState } from "react";
import { DownloadIcon, ShieldCheckIcon } from "lucide-react";

import {
  exportAuditCsv,
  useAuditActions,
  useAuditLog,
} from "@/api/audit";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/utils";

const PAGE_SIZE = 50;

interface Filters {
  action: string;
  actor: string;
  from: string;
  to: string;
}

function actionCategory(action: string): string {
  if (action.startsWith("auth") || action === "login" || action === "logout")
    return "auth";
  if (action.startsWith("camera") || action.startsWith("ptz"))
    return "camera";
  if (
    action === "clip_downloaded"
    || action === "audit_log_exported"
    || action === "report_downloaded"
    || action === "report_generated"
    || action.startsWith("nvr_clip")
  )
    return "export";
  if (
    action.startsWith("user")
    || action.startsWith("role")
    || action.startsWith("api_token")
  )
    return "admin";
  return "other";
}

function ActionBadge({ action }: { action: string }) {
  const cat = actionCategory(action);
  const color: Record<string, string> = {
    auth: "bg-blue-100 text-blue-800 border-blue-200",
    camera: "bg-green-100 text-green-800 border-green-200",
    export: "bg-orange-100 text-orange-800 border-orange-200",
    admin: "bg-purple-100 text-purple-800 border-purple-200",
    other: "bg-gray-100 text-gray-800 border-gray-200",
  };
  return (
    <span className={`rounded border px-2 py-0.5 font-mono text-[11px] ${color[cat]}`}>
      {action}
    </span>
  );
}


function DetailsViewer({ details }: { details: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const empty = !details || Object.keys(details).length === 0;
  if (empty) return <span className="text-muted-foreground">—</span>;
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen((x) => !x)}>
        {open ? "Hide" : "View"}
      </Button>
      {open && (
        <pre className="mt-1 max-w-md overflow-x-auto rounded bg-muted p-2 text-[11px]">
          {JSON.stringify(details, null, 2)}
        </pre>
      )}
    </>
  );
}

function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function AuditLogPage() {
  const [pending, setPending] = useState<Filters>({
    action: "", actor: "", from: "", to: "",
  });
  const [applied, setApplied] = useState<Filters>(pending);
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const { data: actions } = useAuditActions();
  const { data, isLoading, isError, error, refetch } = useAuditLog({
    action: applied.action || undefined,
    actor: applied.actor || undefined,
    from: applied.from ? new Date(applied.from).toISOString() : undefined,
    to: applied.to ? new Date(applied.to).toISOString() : undefined,
    limit: PAGE_SIZE,
    offset,
  });

  // T2c — `total` is only a COUNT once the fetch has succeeded. Interpolating
  // `data?.total ?? 0` into the header meant a failed request printed
  // "0 entries" under a "tamper-evident activity record" heading: an assertion
  // that nothing had happened, made by a request that never landed. Prod holds
  // 5,384 audit rows for this tenant, newest minutes old.
  const total = data?.total ?? 0;
  const countLabel = isError
    ? "Entries could not be counted."
    : isLoading
      ? "Counting…"
      : `${total} entries.`;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  const onApply = () => {
    setApplied(pending);
    setOffset(0);
  };
  const onClear = () => {
    const empty = { action: "", actor: "", from: "", to: "" };
    setPending(empty);
    setApplied(empty);
    setOffset(0);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAuditCsv({
        action: applied.action || undefined,
        actor: applied.actor || undefined,
        from: applied.from ? new Date(applied.from).toISOString() : undefined,
        to: applied.to ? new Date(applied.to).toISOString() : undefined,
      });
      downloadBlob(blob, "audit-export.csv");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ShieldCheckIcon className="h-6 w-6" />
            Audit Log
          </h1>
          <p className="text-sm text-muted-foreground">
            Tamper-evident, hash-chained activity record. {countLabel}
          </p>
        </div>
        <Button onClick={onExport} disabled={exporting}>
          <DownloadIcon className="mr-2 h-4 w-4" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>
      {total > 10_000 && (
        <p className="text-xs text-amber-600">
          ⚠ Result exceeds 10,000 rows. CSV export is capped at 10,000;
          tighten the filters above to get the rest.
        </p>
      )}

      <Card>
        <CardContent className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="f_action">Action</Label>
            <select
              id="f_action"
              value={pending.action}
              onChange={(e) => setPending({ ...pending, action: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All actions</option>
              {(actions ?? []).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_actor">Actor email</Label>
            <Input
              id="f_actor"
              value={pending.actor}
              onChange={(e) => setPending({ ...pending, actor: e.target.value })}
              placeholder="search by email…"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_from">From</Label>
            <Input
              id="f_from"
              type="datetime-local"
              value={pending.from}
              onChange={(e) => setPending({ ...pending, from: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_to">To</Label>
            <Input
              id="f_to"
              type="datetime-local"
              value={pending.to}
              onChange={(e) => setPending({ ...pending, to: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex items-end gap-2 md:col-span-4">
            <Button onClick={onApply} size="sm">Apply filters</Button>
            <Button onClick={onClear} variant="outline" size="sm">Clear</Button>
          </div>
        </CardContent>
      </Card>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="the audit log"
        onRetry={refetch}
      >
        {data && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Timestamp</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Action</th>
                  <th className="px-4 py-3 text-left">Resource</th>
                  <th className="px-4 py-3 text-left">IP</th>
                  <th className="px-4 py-3 text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b align-top last:border-0">
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {row.actor_email ?? (row.user_id ? "(unknown)" : "System")}
                    </td>
                    <td className="px-4 py-2">
                      <ActionBadge action={row.action} />
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {row.resource_type
                        ? `${row.resource_type}${row.resource_id ? `:${row.resource_id.slice(0, 8)}…` : ""}`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 font-mono text-[11px]">
                      {row.ip_address ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <DetailsViewer details={row.details} />
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No entries match those filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
        )}
      </QueryState>

      {data && total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {pageStart}-{pageEnd} of {total} entries
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
